import './ViewDialog.scss';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useAxiosHook from '../../../../../hooks/useAxiosHook';
import useMediaQuery from '../../../../../hooks/useMediaQuery';
import { WithData } from '../../../../../types/api';
import { EntityIdRouteParams } from '../../../../../types/routing';
import DialogSpinner from '../../../../Dialogs/DialogSpinner/DialogSpinner';
import { SortingMachinesType } from '../../SortingMachine.functions';
import { SortingMapsType } from '../../SortingMaps/SortingMaps.functions';

type Props = {
  isShown: boolean;
  onHide: () => void;
  isEditBtnShown: boolean;
  setIsEditDialog: (isEditDialog: SortingMachinesType) => void;
};

function ViewDialog({
  isShown,
  onHide,
  isEditBtnShown,
  setIsEditDialog,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<EntityIdRouteParams>();
  const isOnMobile = useMediaQuery('(max-width: 768px)');

  const { data: dataSortingMachine, isLoading: isLoadingSortingMachine } =
    useAxiosHook<SortingMachinesType>(`/orders/sorting/machine/${id}`, {
      skipWhen: !isShown && !id,
    });

  const { data: dataSortingMaps, isLoading: isLoadingSortingMaps } =
    useAxiosHook<WithData<SortingMapsType[]>>(
      `/orders/sorting/machine/${id}/map`,
      {
        skipWhen: !isShown && !id,
      }
    );

  const dialogFooter = !isLoadingSortingMachine && !isLoadingSortingMaps && (
    <>
      {isEditBtnShown && (
        <Button
          label={t('Edit')}
          className="p-button-text"
          onClick={() => setIsEditDialog(dataSortingMachine!)}
        />
      )}
      <Button label={t('Close')} onClick={() => onHide()} />
    </>
  );

  return (
    <Dialog
      header={
        isLoadingSortingMachine && isLoadingSortingMaps
          ? t('Loading...')
          : t('Preview on sorting machine')
      }
      footer={dialogFooter}
      visible={isShown}
      resizable={false}
      onHide={onHide}
      maximizable
      maximized={isOnMobile}
      style={{ width: 580, maxWidth: '100%' }}
      className="sorting-machines-view-dialog"
    >
      {isLoadingSortingMachine && isLoadingSortingMaps ? (
        <DialogSpinner />
      ) : (
        <>
          <span className="section-title">{t('General information')}</span>
          <table cellPadding="0" cellSpacing="0">
            <tbody>
              <tr>
                <td>{t('Sorting machine name')}</td>
                <td className="route-name">{dataSortingMachine?.name}</td>
              </tr>
              <tr>
                <td>{t('Hub Name')}</td>
                <td className="route-name">{dataSortingMachine?.hubName}</td>
              </tr>
              <tr>
                <td>{t('Max Ports')}</td>
                <td className="route-name">{dataSortingMachine?.maxPorts}</td>
              </tr>
              <tr>
                <td>{t('Employee')}</td>
                <td className="route-name">{dataSortingMachine?.employeeName ?? '-'}</td>
              </tr>
            </tbody>
          </table>
          <span className="section-title">{t('Sorting Maps')}</span>
          <table cellPadding="0" cellSpacing="0">
            <tbody>
              {dataSortingMaps && dataSortingMaps?.data?.length ? (
                dataSortingMaps?.data.map((map) => (
                  <tr key={map.id}>
                    <td>{t('Sorting map name')}</td>
                    <td className="route-name">{map.name}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2}>{t('No sorting maps found')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </Dialog>
  );
}

export default ViewDialog;
